import { Injectable, signal } from '@angular/core';
import { Subject } from 'rxjs';

export interface TimelineScrollEvent {
    sourceId: string;
    // Scroll position expressed as a fraction of the timeline's total width (0..1),
    // so instances zoomed at different scales stay aligned on the same point in time.
    ratio: number;
}

/**
 * Shared state for the report page (project-pages/report), coordinating concerns that
 * cut across its several display modes (timeline, list, calendar) and the timeline
 * instances rendered side by side within the timeline mode.
 *
 * Scroll sync and the hover-popup coordination are the first slices of state living
 * here; further report-wide state (e.g. the active display mode) belongs in this same
 * store rather than spawning another single-purpose one.
 */
@Injectable({
    providedIn: 'root'
})
export class ReportStore {
    // Id of the timeline whose hover popup is currently open ('' when none): only one
    // popup may be open at a time across the several timeline instances in the report.
    readonly activePopup = signal<string>('');

    private readonly timelineScrollSubject = new Subject<TimelineScrollEvent>();
    readonly timelineScrollChanges$ = this.timelineScrollSubject.asObservable();

    openPopup(id: string) {
        this.activePopup.set(id);
    }

    closePopup(id: string) {
        if (this.activePopup() === id)
            this.activePopup.set('');
    }

    broadcastTimelineScroll(sourceId: string, ratio: number) {
        this.timelineScrollSubject.next({ sourceId, ratio });
    }
}
